import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { IFieldDefinition } from "../interfaces";

interface IRecordCardProps {
  title: string;
  subtitle?: string;
  chips?: string[];
  trailing?: React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
  imageUrl?: string;
  record?: Record<string, any>;
  fields?: IFieldDefinition<any>[];
  hiddenFieldKeys?: string[];
  compact?: boolean;
}
const getRelationObjectKey = (fieldKey: string): string => {
  if (fieldKey.endsWith("Uuid")) {
    return fieldKey.slice(0, -4);
  }

  if (fieldKey.endsWith("Uid")) {
    return fieldKey.slice(0, -3);
  }

  return fieldKey;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

export const RecordCard = (props: IRecordCardProps) => {
  const {
    title,
    subtitle,
    chips,
    trailing,
    onClick,
    selected,
    imageUrl,
    record,
    fields = [],
    hiddenFieldKeys = ["name", "description", "imageUrl"],
    compact = false,
  } = props;

  const hiddenKeysSet = new Set(hiddenFieldKeys);

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "string") {
      if (value.includes("_")) {
        return value.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
      }

      return value;
    }

    return String(value);
  };

  const getRelationDisplayValue = (field: IFieldDefinition<any>): string => {
    if (!record) {
      return "-";
    }

    const relationObjectKey = getRelationObjectKey(String(field.key));
    const relationObject = record[relationObjectKey];

    if (!isPlainObject(relationObject)) {
      return "-";
    }

    const displayValue = relationObject.name ?? relationObject.label ?? relationObject.title ?? relationObject.range ?? relationObject.uid;

    return formatValue(displayValue);
  };

  const renderFieldValue = (field: IFieldDefinition<any>, value: any, fieldPath: string): React.ReactNode => {
    if (field.type === "relation") {
      return <Typography variant="body2">{getRelationDisplayValue(field)}</Typography>;
    }

    if (value === null || value === undefined || value === "") {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    if (field.type === "object" && field.fields?.length) {
      return (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {field.fields.map((nestedField) => {
            const nestedKey = String(nestedField.key);
            const nestedValue = value?.[nestedKey];

            return (
              <Box key={`${fieldPath}.${nestedKey}`}>
                <Typography variant="caption" color="text.secondary">
                  {nestedField.label}
                </Typography>

                {nestedField.type === "object" ? (
                  renderFieldValue(nestedField, nestedValue, `${fieldPath}.${nestedKey}`)
                ) : (
                  <Typography variant="body2">{formatValue(nestedValue)}</Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      );
    }

    if (field.type === "image") {
      return null;
    }

    return <Typography variant="body2">{formatValue(value)}</Typography>;
  };

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        borderRadius: compact ? 2 : 3,
        cursor: onClick ? "pointer" : "default",
        borderColor: selected ? "primary.main" : "divider",
        boxShadow: selected ? 2 : "none",
        overflow: "hidden",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
        bgcolor: selected ? "action.selected" : "background.paper",
        "&:hover": onClick
          ? {
              borderColor: "primary.main",
              bgcolor: selected ? "action.selected" : "action.hover",
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          display: compact ? "flex" : "block",
          alignItems: compact ? "stretch" : undefined,
        }}
      >
        {imageUrl ? (
          <Box
            sx={{
              width: compact ? 68 : "100%",
              height: compact ? "auto" : 220,
              minHeight: compact ? 68 : undefined,
              flexShrink: 0,
              bgcolor: "grey.100",
              borderRight: compact ? "1px solid" : undefined,
              borderBottom: compact ? undefined : "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: compact ? 0.75 : 1,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                display: "block",
                width: "100%",
                height: compact ? 52 : "100%",
                maxHeight: compact ? 52 : undefined,
                objectFit: "contain",
              }}
            />
          </Box>
        ) : null}

        <CardContent
          sx={{
            flex: 1,
            minWidth: 0,
            p: compact ? 1 : 2,
            "&:last-child": {
              pb: compact ? 1 : 2,
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={compact ? 1 : 2}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant={compact ? "body2" : "subtitle2"}
                fontWeight={700}
                noWrap
                sx={{
                  lineHeight: compact ? 1.2 : 1.4,
                }}
              >
                {title}
              </Typography>

              {subtitle && (
                <Typography
                  variant={compact ? "caption" : "body2"}
                  color="text.secondary"
                  noWrap={compact}
                  sx={{
                    mt: compact ? 0.25 : 0.5,
                    lineHeight: compact ? 1.15 : 1.4,
                  }}
                >
                  {subtitle}
                </Typography>
              )}

              {chips && chips.length > 0 && (
                <Stack
                  direction="row"
                  spacing={compact ? 0.5 : 1}
                  sx={{
                    mt: compact ? 0.75 : 1,
                    flexWrap: "wrap",
                    gap: compact ? 0.5 : 1,
                  }}
                >
                  {chips.map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      size="small"
                      variant="outlined"
                      sx={
                        compact
                          ? {
                              height: 20,
                              fontSize: "0.68rem",
                              "& .MuiChip-label": {
                                px: 0.75,
                              },
                            }
                          : undefined
                      }
                    />
                  ))}
                </Stack>
              )}

              {record && fields.length > 0 && (
                <Stack spacing={compact ? 0.75 : 1.25} sx={{ mt: compact ? 1 : 1.5 }}>
                  {fields
                    .filter((field) => !hiddenKeysSet.has(String(field.key)))
                    .map((field) => {
                      const fieldKey = String(field.key);
                      const value = record[fieldKey];

                      return (
                        <Box key={fieldKey}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              lineHeight: compact ? 1.1 : 1.4,
                              display: "block",
                            }}
                          >
                            {field.label}
                          </Typography>

                          {renderFieldValue(field, value, fieldKey)}
                        </Box>
                      );
                    })}
                </Stack>
              )}
            </Box>

            {trailing ? <Box sx={{ flexShrink: 0 }}>{trailing}</Box> : null}
          </Stack>
        </CardContent>
      </Box>
    </Card>
  );
};
